import React, { useRef } from 'react';

import { FinderType } from '@philips-emr/tws-components-api';
import { ModalType, Placement } from '@philips-emr/tws-constants';
import { arrayOf, bool, element, func, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { enumValues, isFunction } from '@philips-emr/tws-utils';

import { Input, Modal, NotFound, Popover } from '../..';

function Content(props) {
  const { children, className, emptyResultExpression, isEmpty, showImage } = props;

  if (!isEmpty) {
    return (
      <div className="c-finder__content">
        {children}
      </div>
    );
  }

  return (
    <div className="c-finder__content">
      <NotFound className={className} titleExpression={emptyResultExpression} showImage={showImage} />
      {children}
    </div>
  );
}

Content.propTypes = {
  children: oneOfType([element, arrayOf(element)]),
  className: string,
  emptyResultExpression: string,
  isEmpty: bool,
  showImage: bool,
};

function FinderInlineBox(props) {

  return (
    <Content {...props} showImage />
  );
}

FinderInlineBox.propTypes = {
  children: oneOfType([element, arrayOf(element)]),
  emptyResultExpression: string,
  isEmpty: bool,
};

function FinderFieldBox(props) {
  const { enabled, inputValue, onBlur, onChange, onClose, onFocus, onKeyDown, open, placeholder, readOnly } = props;

  const inputRef = useRef(null);

  const onBlurFieldBox = () => {
    if (open) {
      inputRef.current.focusInput();
      return;
    }

    isFunction(onBlur) && onBlur();
  }

  return (
    <Popover
      matchTargetWidth
      onClose={onClose}
      open={open}
      placement={Placement.BOTTOM}
      showArrow={false}
    >
      <div>
        <Input
          className="c-finder__search"
          enabled={enabled}
          onBlur={onBlurFieldBox}
          onChange={onChange}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={msg(placeholder)}
          readOnly={readOnly}
          value={inputValue}
          ref={inputRef}
        />
      </div>

      <Popover.Content>
        <Content {...props} className="c-finder__not-found" showImage={false} />
      </Popover.Content>
    </Popover>
  );
}

FinderFieldBox.propTypes = {
  children: oneOfType([element, arrayOf(element)]),
  emptyResultExpression: string,
  enabled: bool,
  inputValue: string,
  isEmpty: bool,
  onBlur: func,
  onChange: func,
  onClose: func,
  onFocus: func,
  onKeyDown: func,
  open: bool,
  placeholder: string,
  readOnly: bool,
};

function FinderDialogBox(props) {
  const { inputValue, onChange, onClose, onFocus, open, placeholder } = props;

  if (!open) {
    return null;
  }

  return (
    <Modal className="c-finder--dialog" onClose={onClose} title={placeholder} type={ModalType.FULLSCREEN}>
      <Input
        autoFocus
        onChange={onChange}
        onFocus={onFocus}
        placeholder={placeholder}
        value={inputValue}
      />
      <Content {...props} showImage />
    </Modal>
  );
}

FinderDialogBox.propTypes = {
  children: oneOfType([element, arrayOf(element)]),
  emptyResultExpression: string,
  inputValue: string,
  isEmpty: bool,
  onBlur: func,
  onChange: func,
  onClose: func,
  onFocus: func,
  open: bool,
  placeholder: string,
};

function FinderBox(props) {
  const { type, ...rest } = props;

  if (type === FinderType.INLINE) {
    return <FinderInlineBox {...rest} />;
  }

  if (type === FinderType.FIELD) {
    return <FinderFieldBox {...rest} />;
  }

  if (type === FinderType.DIALOG) {
    return <FinderDialogBox {...rest} />;
  }

  return null;
}

FinderBox.propTypes = {
  children: oneOfType([element, arrayOf(element)]),
  type: oneOf(enumValues(FinderType)),
};

export default FinderBox;
